import type { DocumentoEmail } from '@email/types.ts';

/**
 * Le sequenze automatiche: un percorso di caselle che il contatto attraversa
 * da solo. Rispecchia le tabelle email_automation_* del database.
 */

export type AutomationStatus = 'bozza' | 'attiva' | 'sospesa';
export type TriggerKind = 'manuale' | 'contatto_attivato' | 'etichetta_aggiunta';
export type StepKind =
  | 'invio'
  | 'attesa'
  | 'attendi_evento'
  | 'condizione'
  | 'etichetta'
  | 'ingresso_evento'
  | 'fine';
export type TipoDomanda = 'ha_cliccato' | 'ha_risposto' | 'ha_ricevuto' | 'ha_etichetta' | 'prima_di';
/** Per le sequenze che partono da una lista: entra tutta, o solo chi viene
 *  aggiunto dopo l'accensione. */
export type ChiDellaLista = 'tutta' | 'nuovi';

export interface EmailAutomation {
  id: string;
  name: string;
  description: string | null;
  status: AutomationStatus;
  trigger_kind: TriggerKind;
  trigger_config: { tag_id?: string; chi?: ChiDellaLista };
  first_step_id: string | null;
  ripetibile: boolean;
  /** Informazioni sul corso agli iscritti: arriva anche a chi non ha
   *  accettato le promozioni (migrazione 29). Il database lo da' sempre;
   *  assente vale "promozioni". */
  servizio?: boolean;
  entrati_count: number;
  finiti_count: number;
  created_at: string;
  updated_at: string;
}

export interface ConfigInvio {
  subject: string;
  preheader?: string | null;
  document: DocumentoEmail;
}

export interface ConfigAttesa {
  minuti: number;
}

export interface ConfigCondizione {
  tipo: TipoDomanda;
  /** La email a cui si riferisce la domanda. Vuoto = una qualsiasi. */
  step_id?: string | null;
  tag_id?: string | null;
  /** Per "E' prima del giorno?": il giorno, come AAAA-MM-GG. */
  data?: string | null;
}

/** "Aspetta che entri in una lista". */
export interface ConfigAttendiEvento {
  tag_id: string | null;
  /** Il tempo massimo, in minuti. Null = aspetta senza scadenza. */
  max_minuti: number | null;
}

/** "Ogni volta che entra in una lista": la partenza di un'azione a parte. */
export interface ConfigIngresso {
  tag_id: string | null;
}

/** Cosa fa la casella lista: controlla, oppure sposta il contatto. */
export type AzioneLista = 'solo_se_dentro' | 'aggiungi' | 'togli';

export interface ConfigEtichetta {
  tag_id: string | null;
  azione: AzioneLista;
}

export type ConfigPasso =
  | ConfigInvio
  | ConfigAttesa
  | ConfigAttendiEvento
  | ConfigCondizione
  | ConfigEtichetta
  | ConfigIngresso
  | Record<string, never>;

export interface EmailAutomationStep {
  id: string;
  automation_id: string;
  kind: StepKind;
  label: string | null;
  config: ConfigPasso;
  next_id: string | null;
  /** Il ramo "no" di una domanda, o "Scaduto" di un'attesa di una lista.
   *  Sulle altre caselle non si usa. */
  next_alt_id: string | null;
  pos_x: number;
  pos_y: number;
}

/** Un contatto dentro una sequenza. */
export interface EmailAutomationRun {
  id: string;
  automation_id: string;
  contact_id: string;
  step_id: string | null;
  state: 'in_corso' | 'finita' | 'ferma';
  /** Puo' valere 'infinity': aspetta una lista senza scadenza. */
  next_at: string;
  motivo_stop: string | null;
  entered_at: string;
  finished_at: string | null;
  /** Figlia di un'azione "ogni volta": la percorrenza principale. Null = principale. */
  parent_run_id: string | null;
  /** La casella "ogni volta" da cui e' nata la figlia. */
  ingresso_step_id: string | null;
  /** Quando e' arrivata sulla casella attuale. */
  arrivato_at: string;
  attesa_evento_da: string | null;
  evento_at: string | null;
  scade_at: string | null;
}

// --- Etichette per l'interfaccia ------------------------------------------

export const AUTOMATION_STATUS: Record<
  AutomationStatus,
  { label: string; spiegazione: string; badge: string; accento: string }
> = {
  bozza: {
    label: 'Bozza',
    spiegazione: 'Si sta ancora disegnando. Nessuno ci sta dentro.',
    badge: 'bg-neutral-100 text-neutral-600 border-neutral-200',
    accento: 'bg-neutral-300',
  },
  attiva: {
    label: 'Attiva',
    spiegazione: 'In funzione: i contatti ci entrano e ricevono.',
    badge: 'bg-green-50 text-green-700 border-green-200',
    accento: 'bg-green-500',
  },
  sospesa: {
    label: 'In pausa',
    spiegazione: 'Nessuno entra e nessuno avanza. Chi e\' dentro resta dov\'e\'.',
    badge: 'bg-orange-50 text-orange-700 border-orange-200',
    accento: 'bg-orange-500',
  },
};

export const TRIGGER: Record<TriggerKind, { label: string; spiegazione: string }> = {
  contatto_attivato: {
    label: 'Quando un contatto da\' il consenso',
    spiegazione: 'Entra chi diventa attivo: iscrizione dal sito, conferma, importazione con consenso.',
  },
  etichetta_aggiunta: {
    label: 'Da una lista',
    spiegazione: 'Entrano i contatti di una lista che scegli: tutta la lista, o solo chi aggiungi dopo.',
  },
  manuale: {
    label: 'Solo quando lo decido io',
    spiegazione: 'I contatti li scegli tu dalla lista e li fai partire a mano.',
  },
};

export const CHI_DELLA_LISTA: Record<ChiDellaLista, { label: string; spiegazione: string }> = {
  tutta: {
    label: 'Tutta la lista',
    spiegazione:
      'Quando la accendi entrano subito tutti quelli che sono gia\' nella lista. Chi aggiungi dopo entra appena lo aggiungi.',
  },
  nuovi: {
    label: 'Solo chi aggiungo da ora in poi',
    spiegazione:
      'Chi e\' gia\' nella lista non riceve niente. Quando aggiungi qualcuno, la sequenza parte solo per lui.',
  },
};

/** Cosa manda la sequenza: decide a chi puo' arrivare. */
export const TIPO_SEQUENZA: Record<
  'promozioni' | 'servizio',
  { label: string; spiegazione: string }
> = {
  promozioni: {
    label: 'Promozioni e novita\'',
    spiegazione: 'Corsi, servizi, eventi, offerte. Arriva solo a chi ha accettato di ricevere le mail.',
  },
  servizio: {
    label: 'Informazioni sul corso',
    spiegazione:
      'Per chi e\' iscritto: date, sede, documenti, cosa portare. Arriva anche a chi non ha accettato le promozioni, mai a chi si e\' disiscritto.',
  },
};

/** Nome e spiegazione di ogni casella. Il colore sta in aspettoPassi.ts. */
export const PASSI: Record<StepKind, { label: string; spiegazione: string }> = {
  invio: {
    label: 'Manda una email',
    spiegazione: 'Compone e spedisce. La quota giornaliera vale anche qui.',
  },
  attesa: {
    label: 'Aspetta',
    spiegazione: 'Ferma il contatto per un po\'. Se cade di notte o di domenica, slitta.',
  },
  attendi_evento: {
    label: 'Aspetta che entri in una lista',
    spiegazione:
      'Ferma il contatto finche\' non viene messo in una lista (es. "richiesta inviata"). Con un tempo massimo, se scade prende l\'altra strada.',
  },
  condizione: {
    label: 'Fai una domanda',
    spiegazione: 'Divide il percorso in due: chi risponde si\' di qua, chi no di la\'.',
  },
  etichetta: {
    label: 'Controlla o cambia una lista',
    spiegazione:
      'Fa andare avanti solo chi e\' in una lista scelta. Oppure mette o toglie il contatto da una lista.',
  },
  ingresso_evento: {
    label: 'Ogni volta che entra in una lista',
    spiegazione:
      'Un\'azione a parte: ogni volta che il contatto viene messo nella lista fa le caselle collegate sotto, e lo lascia dov\'era nel percorso.',
  },
  fine: {
    label: 'Fine',
    spiegazione: 'Il percorso finisce qui.',
  },
};

export const DOMANDE: Record<TipoDomanda, { label: string; spiegazione: string }> = {
  ha_cliccato: {
    label: 'Ha cliccato un link?',
    spiegazione: 'Il segnale piu\' affidabile che c\'e\': un clic lo fa una persona.',
  },
  ha_risposto: {
    label: 'Ha risposto?',
    spiegazione:
      'Si\' se ha risposto alla email. La risposta arriva comunque alla casella di chi scrive, inoltrata dal gestionale.',
  },
  ha_ricevuto: {
    label: 'Gli e\' arrivata?',
    spiegazione: 'Consegnata davvero, non rimbalzata.',
  },
  ha_etichetta: {
    label: 'E\' in questa lista?',
    spiegazione: 'Guarda in quali liste e\' il contatto in questo momento.',
  },
  prima_di: {
    label: 'E\' prima del giorno...?',
    spiegazione:
      'Si\' fino al giorno prima di quello scelto, no dal giorno stesso in poi. Serve a fermare i promemoria quando le scelte chiudono o l\'evento e\' passato.',
  },
};

/** Le tre cose che fa la casella lista, nell'ordine in cui si propongono. */
export const AZIONI_LISTA: Record<AzioneLista, { label: string; spiegazione: string }> = {
  solo_se_dentro: {
    label: 'Va avanti solo se e\' nella lista',
    spiegazione:
      'Chi e\' nella lista prosegue. Chi non c\'e\' esce dalla sequenza qui e non riceve le email successive.',
  },
  aggiungi: {
    label: 'Mettilo nella lista',
    spiegazione: 'Lo aggiunge alla lista e prosegue. Non gli manda niente.',
  },
  togli: {
    label: 'Toglilo dalla lista',
    spiegazione: 'Lo toglie dalla lista e prosegue. Resta in rubrica e iscritto.',
  },
};

/**
 * Le aperture non sono fra le domande possibili, e non e' una dimenticanza:
 * Apple Mail pre-carica i pixel di tracciamento, quindi un'apertura puo'
 * essere di Apple e non di una persona. Restano nelle statistiche, ma non si
 * decide su quelle a chi mandare la mail successiva.
 */
export const PERCHE_NIENTE_APERTURE =
  'Le aperture non si possono usare per decidere: Apple Mail le finge caricando i pixel da solo.';
